import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import { WarningDiamondIcon, InfoIcon } from "@phosphor-icons/react";

/**
 * Diferencias detectadas entre los apartados que se unificaron (autorizaciones por documento,
 * campos CAI anteriores de Datos de facturación y "Configuración general"). Nada se sobrescribe
 * solo: el administrador elige qué hacer con cada diferencia y la decisión queda auditada.
 */
const ACCION = {
  registrar_historico: "Registrar como autorización histórica",
  descartar: "Descartar (no usar)",
  conservar: "Conservar lo actual",
  usar_anterior: "Usar el valor anterior",
  aplicar_direccion_comun: "Aplicar dirección común",
};

export default function FiscalReview({ isAdmin, onResolved }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/fiscal-review");
      setItems(data.diferencias || []);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolver = async (d, accion) => {
    setBusy(d.clave);
    try {
      await api.post(`/settings/fiscal-review/${d.clave}`, { accion });
      toast.success("Diferencia resuelta");
      await load(); onResolved?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  if (!items.length) return null;
  return (
    <div className="bg-agro-yellow/5 border border-agro-yellow/40 rounded-lg overflow-hidden" data-testid="fiscal-review">
      <div className="px-5 py-3 border-b border-agro-yellow/30 flex gap-3 items-start">
        <WarningDiamondIcon size={20} className="text-agro-yellow" weight="duotone" />
        <div>
          <div className="font-heading text-agro-yellow">Diferencias detectadas al unificar ({items.length})</div>
          <div className="text-[11px] text-text-muted mt-0.5">
            Los datos fiscales estaban en varios apartados. Revisa cada diferencia: nada se sobrescribió
            automáticamente y ningún dato se borra.
          </div>
        </div>
      </div>
      <div className="divide-y divide-border/40">
        {items.map((d) => (
          <div key={d.clave} className="p-4" data-testid={`review-${d.clave}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {d.severidad === "informativa" ? <InfoIcon size={16} className="text-blue-300" /> : <WarningDiamondIcon size={16} className="text-agro-yellow" />}
              {d.titulo}
            </div>
            <div className="text-[11px] text-text-muted mt-1">{d.descripcion}</div>
            <Valores v={d.valores} />
            <div className="flex gap-2 mt-2 flex-wrap">
              {d.acciones.map((a) => (
                <button key={a} type="button" disabled={!isAdmin || busy === d.clave} onClick={() => resolver(d, a)}
                  data-testid={`review-${d.clave}-${a}`}
                  className="text-xs border border-border rounded-md px-3 py-1.5 hover:border-agro-green hover:text-agro-green disabled:opacity-40">
                  {ACCION[a] || a}
                </button>
              ))}
              {!isAdmin && <span className="text-[11px] text-text-muted self-center">Solo el administrador puede resolverla.</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Valores({ v }) {
  const rows = [];
  if (v.cai) rows.push(["CAI", v.cai]);
  if (v.rango_inicial || v.rango_final) rows.push(["Rango", `${v.rango_inicial || "—"} a ${v.rango_final || "—"}`]);
  if (v.fecha_limite) rows.push(["Fecha límite", v.fecha_limite]);
  Object.entries(v.diferencias || {}).forEach(([k, [a, b]]) => rows.push([`${k} (anterior → registrado)`, `${a} → ${b}`]));
  if (v.etiqueta) rows.push([`${v.etiqueta} anterior`, v.anterior], [`${v.etiqueta} actual`, v.actual || "—"]);
  if (v.direccion_autorizacion) rows.push(["Dirección de la autorización", v.direccion_autorizacion], ["Dirección común", v.direccion_comun || "—"]);
  return (
    <div className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-[11px]">
      {rows.map(([k, val]) => (
        <div key={k} className="contents"><span className="text-text-muted">{k}</span><span className="font-mono break-all">{val}</span></div>
      ))}
    </div>
  );
}
